import DOMPurify from "dompurify"
import { marked } from "marked"
import { memo, useEffect, useState } from "react"

import { safeImageSrc } from "~/components/base-editor/plugins/image-src-allowlist"
import { track } from "~/utils/amplitude"
import { EVENTS, getBaseEventProps } from "~/utils/amplitude-events"
import { isHTML } from "~/utils/helpers"

// Lazy, not module scope: SSR imports this file where DOMPurify has no addHook.
let sanitizeHooksRegistered = false
const registerSanitizeHooks = () => {
  if (sanitizeHooksRegistered || typeof DOMPurify.addHook !== "function") return
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.nodeName === "A") {
      const href = node.getAttribute("href") || ""
      if (/^https?:\/\//i.test(href)) {
        node.setAttribute("rel", "noopener noreferrer nofollow")
        node.setAttribute("target", "_blank")
      }
      return
    }
    // Drop image/media src to non-allowlisted hosts (ENG-3882 exfiltration on load).
    for (const attr of ["src", "poster"]) {
      const value = node.getAttribute(attr)
      if (value && !safeImageSrc(value)) node.removeAttribute(attr)
    }
    const style = node.getAttribute("style")
    if (style && /url\s*\(/i.test(style)) {
      node.setAttribute("style", style.replace(/url\s*\([^)]*\)/gi, ""))
    }
  })
  sanitizeHooksRegistered = true
}

const sanitizeMarkdownHtml = (html: string) => {
  registerSanitizeHooks()
  return DOMPurify.sanitize(html, {
    ADD_TAGS: ["video", "source"],
    ADD_ATTR: [
      "autoplay",
      "controls",
      "dir",
      "height",
      "loop",
      "muted",
      "playsinline",
      "poster",
      "preload",
      "style",
      "src",
      "type",
      "width"
    ]
  })
}

const enableVideoAutoplay = (html: string) => {
  const template = document.createElement("template")
  template.innerHTML = html

  template.content.querySelectorAll("video").forEach((video) => {
    video.setAttribute("autoplay", "autoplay")
    video.setAttribute("muted", "muted")
    video.setAttribute("playsinline", "playsinline")
    video.setAttribute("preload", "metadata")
  })

  return template.innerHTML
}

interface MarkdownRenderProps {
  content: string
  className?: string
  editorInstanceRef?: any
  editable?: boolean
  enableVideoAutoplay?: boolean
  onClick?: () => void
}

const MarkdownRender = memo(
  ({
    content,
    className = "p-3",
    editorInstanceRef,
    editable = false,
    enableVideoAutoplay: shouldEnableVideoAutoplay = false,
    onClick,
    ...rest
  }: MarkdownRenderProps & {
    "data-testid"?: string
  }) => {
    const [sanitizedHtml, setSanitizedHtml] = useState<string>("")

    useEffect(() => {
      let isActive = true
      const parseMarkdown = async () => {
        const html = await marked.parse(content || "")
        if (isActive) {
          const sanitized = sanitizeMarkdownHtml(
            shouldEnableVideoAutoplay ? enableVideoAutoplay(html) : html
          )
          setSanitizedHtml(sanitized)
        }
      }

      if (isHTML(content)) {
        const html = shouldEnableVideoAutoplay
          ? enableVideoAutoplay(content)
          : content
        setSanitizedHtml(sanitizeMarkdownHtml(html))
      } else {
        parseMarkdown()
      }

      return () => {
        isActive = false
      }
    }, [content, shouldEnableVideoAutoplay])

    return (
      <div
        className={`${className} markdown outline-none text-xs`}
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        ref={editorInstanceRef}
        contentEditable={editable}
        onClick={() => {
          if (onClick) {
            track(EVENTS.COMPONENTS.MARKDOWN_RENDER_CLICKED, {
              ...getBaseEventProps({ feature: "components" })
            })
            onClick()
          }
        }}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && onClick) {
            e.preventDefault()
            track(EVENTS.COMPONENTS.MARKDOWN_RENDER_CLICKED, {
              ...getBaseEventProps({ feature: "components" })
            })
            onClick()
          }
        }}
        data-testid={rest["data-testid"]}
      />
    )
  }
)
MarkdownRender.displayName = "MarkdownRender"
export default MarkdownRender
